import React, { useEffect, useState } from "react";
import { TrendingDown, Plus, Loader2, X, Save } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";

const CATS = ["CASHBACK", "PAYMENT_FEE", "REFUND", "FRAUD", "CHARGEBACK", "REVERSAL", "OPERATIONAL", "OTHER"];

export default function Loss() {
  const [sum, setSum] = useState(null);
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ category: "OPERATIONAL", amount_rupees: "", reason: "", related_merchant: "", related_customer: "" });
  const [busy, setBusy] = useState(false);

  const loadSum = () => api.get("/admin/loss/summary").then((r) => setSum(r.data)).catch(() => {});
  const loadEntries = () => {
    const params = filter ? { category: filter } : {};
    api.get("/admin/loss/entries", { params }).then((r) => setEntries(r.data.entries)).catch(() => {});
  };
  useEffect(() => { loadSum(); }, []);
  useEffect(() => { loadEntries(); /* eslint-disable-next-line */ }, [filter]);

  const submit = async () => {
    if (!form.amount_rupees || Number(form.amount_rupees) <= 0) { toast.error("Enter a valid amount"); return; }
    if (!form.reason.trim()) { toast.error("Reason required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/loss/entries", {
        category: form.category, amount_paise: Math.round(Number(form.amount_rupees) * 100),
        reason: form.reason.trim(), related_merchant: form.related_merchant, related_customer: form.related_customer,
      });
      toast.success("Loss entry recorded");
      setModal(false); setForm({ category: "OPERATIONAL", amount_rupees: "", reason: "", related_merchant: "", related_customer: "" });
      loadSum(); loadEntries();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  if (!sum) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-loss">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><TrendingDown className="h-5 w-5 text-orange-400" /><h1 className="font-heading text-2xl font-bold">Loss &amp; exposure</h1></div>
        <button data-testid="loss-add-btn" onClick={() => setModal(true)} className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-orange-400"><Plus className="h-4 w-4" /> Record loss</button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card label="Total loss" value={sum.total.display} accent testid="loss-total" />
        <Card label="Today" value={sum.today.display} testid="loss-today" />
        <Card label="This month" value={sum.this_month.display} testid="loss-month" />
        <Card label="Previous month" value={sum.prev_month.display} testid="loss-prevmonth" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="ledger-card rounded-2xl p-5">
          <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Auto-computed</p>
          <div className="mt-3 space-y-2 text-sm">
            <Row label="Cashback cost" value={sum.auto.cashback_cost.display} />
            <Row label="Payment fees" value={sum.auto.payment_fees.display} muted />
            <Row label="Refunds" value={sum.auto.refunds.display} />
            <Row label="Reversal (recovered)" value={sum.auto.reversal.display} />
            <div className="border-t border-slate-800 pt-2"><Row label="Manual entries total" value={sum.manual_total.display} strong /></div>
          </div>
        </div>
        <div className="ledger-card rounded-2xl p-5 lg:col-span-2">
          <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">By category</p>
          <div className="mt-3 space-y-2">
            {sum.categories.map((c) => (
              <div key={c.category} className="flex items-center justify-between text-sm">
                <span className="text-slate-300">{c.category}<span className="ml-2 text-xs text-slate-600">auto {(c.auto_paise / 100).toLocaleString("en-IN")} · manual {(c.manual_paise / 100).toLocaleString("en-IN")}</span></span>
                <span className="font-mono font-semibold text-orange-300">{c.total_display}</span>
              </div>
            ))}
            {sum.categories.length === 0 && <p className="text-sm text-slate-500">No losses recorded.</p>}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-bold">Manual loss entries</h2>
        <select data-testid="loss-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-full border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm">
          <option value="">All categories</option>{CATS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="ledger-card overflow-hidden rounded-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="loss-entries-table">
            <thead className="bg-slate-900/50 font-mono text-xs uppercase tracking-wider text-slate-500"><tr><th className="p-4 text-left">Date</th><th className="p-4 text-left">Category</th><th className="p-4 text-right">Amount</th><th className="p-4 text-left">Reason</th><th className="p-4 text-left">Status</th></tr></thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.loss_id} className="border-t border-slate-800/60">
                  <td className="p-4 text-slate-400">{new Date(e.created_at).toLocaleString("en-IN")}</td>
                  <td className="p-4 font-mono text-xs text-orange-300">{e.category}</td>
                  <td className="p-4 text-right font-mono text-orange-300">{e.amount_display}</td>
                  <td className="p-4 text-slate-300">{e.reason}</td>
                  <td className="p-4 font-mono text-xs">{e.status}</td>
                </tr>
              ))}
              {entries.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No manual loss entries.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 z-[70] grid place-items-center p-4" data-testid="loss-modal">
          <div className="absolute inset-0 bg-slate-950/70" onClick={() => setModal(false)} />
          <div className="relative w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6">
            <div className="mb-4 flex items-center justify-between"><p className="font-heading text-lg font-bold">Record loss entry</p><button onClick={() => setModal(false)} aria-label="Close" className="text-slate-400"><X className="h-5 w-5" /></button></div>
            <div className="space-y-3">
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-slate-500">Category</label>
                <select data-testid="loss-field-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm outline-none focus:border-orange-500">{CATS.map((c) => <option key={c} value={c}>{c}</option>)}</select>
              </div>
              <div><label className="font-mono text-xs uppercase tracking-widest text-slate-500">Amount ₹</label><input data-testid="loss-field-amount" type="number" value={form.amount_rupees} onChange={(e) => setForm({ ...form, amount_rupees: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm outline-none focus:border-orange-500" /></div>
              <div><label className="font-mono text-xs uppercase tracking-widest text-slate-500">Reason</label><textarea data-testid="loss-field-reason" rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm outline-none focus:border-orange-500" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Merchant (opt)</label><input data-testid="loss-field-merchant" value={form.related_merchant} onChange={(e) => setForm({ ...form, related_merchant: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-2 text-xs outline-none" /></div>
                <div><label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Customer (opt)</label><input data-testid="loss-field-customer" value={form.related_customer} onChange={(e) => setForm({ ...form, related_customer: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-2 text-xs outline-none" /></div>
              </div>
            </div>
            <button data-testid="loss-save-btn" onClick={submit} disabled={busy} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-orange-500 py-2.5 font-semibold text-slate-950 hover:bg-orange-400 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Record</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ label, value, accent, testid }) {
  return (<div className="ledger-card rounded-2xl p-5" data-testid={testid}><p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{label}</p><p className={`mt-2 font-heading text-2xl font-extrabold ${accent ? "text-orange-400" : "text-slate-100"}`}>{value}</p></div>);
}
function Row({ label, value, muted, strong }) {
  return (<div className="flex items-center justify-between"><span className={strong ? "font-semibold text-slate-200" : "text-slate-400"}>{label}</span><span className={`font-mono ${muted ? "text-slate-500" : "text-orange-300"}`}>{value}</span></div>);
}
